import { useMemo, useState, useEffect } from "react";

const TRAINING_BASE = "/assets/Training/";

/**
 * Files per category in public/assets/Training — add filenames under the right key.
 */
const TRAINING_ASSETS = {
  twoWheeler: [
    "Bajaj_2W_Video.mp4",
    "SBIG_2W_OTP_NEW.mp4",
    "Bajaj _2W .pptx",
    "GoDigit_2W.pptx",
    "HDFC_2W .pptx",
    "ICICI Lombard_2W _SAOD.pptx",
    "ICICI Lombard_2W.pptx",
    "Reliance _2W.pptx",
    "Reliance_2W _SAOD Flow.pdf",
    "Reliance_2W _SAOD.pptx",
    "SBIG_2W_OTP_Flow.pptx",
    "SBIG_2W_OVD_Flow.pptx",
  ],
  fourWheeler: [],
  health: [],
};

const TAB_KEYS = ["twoWheeler", "fourWheeler", "health"];
const TAB_LABELS = {
  twoWheeler: "2 Wheeler",
  fourWheeler: "4 Wheeler",
  health: "Health",
};

const TAB_ICONS = {
  twoWheeler: "fa-motorcycle",
  fourWheeler: "fa-car",
  health: "fa-heartbeat",
};

function trainingUrl(filename) {
  return `${TRAINING_BASE}${encodeURIComponent(filename)}`;
}

function displayTitle(filename) {
  const base = filename.replace(/\.[^/.]+$/, "");
  return base.replace(/_/g, " ").trim();
}

function assetKind(filename) {
  const lower = filename.toLowerCase();
  if (/\.(mp4|webm|ogg)$/i.test(lower)) return "video";
  if (/\.pdf$/i.test(lower)) return "pdf";
  if (/\.(ppt|pptx)$/i.test(lower)) return "slides";
  return "other";
}

function bucketFiles(files) {
  const videos = [];
  const pdfs = [];
  const slides = [];
  for (const name of files) {
    const k = assetKind(name);
    if (k === "video") videos.push(name);
    else if (k === "pdf") pdfs.push(name);
    else if (k === "slides") slides.push(name);
  }
  return { videos, pdfs, slides };
}

const Training = () => {
  const [activeTab, setActiveTab] = useState("twoWheeler");
  const [mediaSection, setMediaSection] = useState("videos");
  const [officePreviewOk, setOfficePreviewOk] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const h = window.location.hostname;
    setOfficePreviewOk(h !== "localhost" && h !== "127.0.0.1");
  }, []);

  const activeFiles = TRAINING_ASSETS[activeTab] || [];
  const { videos, pdfs, slides } = useMemo(
    () => bucketFiles(activeFiles),
    [activeFiles]
  );

  const officeEmbedUrl = (filename) => {
    const absolute = new URL(trainingUrl(filename), window.location.origin).href;
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
      absolute
    )}`;
  };

  const hasAnyContent = videos.length > 0 || pdfs.length > 0 || slides.length > 0;

  useEffect(() => {
    setMediaSection((prev) => {
      const counts = {
        videos: videos.length,
        pdfs: pdfs.length,
        slides: slides.length,
      };
      if (counts[prev] > 0) return prev;
      if (videos.length > 0) return "videos";
      if (pdfs.length > 0) return "pdfs";
      if (slides.length > 0) return "slides";
      return "videos";
    });
  }, [activeTab, videos.length, pdfs.length, slides.length]);

  return (
    <main className="training-page">
      <div
        className="container-fluid page-header py-4 py-md-5 mb-4 mb-md-5 wow fadeIn"
        data-wow-delay="0.1s"
      >
        <div className="container py-4 py-md-5">
          <h1 className="display-5 display-md-4 animated slideInDown mb-0 text-break">
            Training
          </h1>
        </div>
      </div>

      <div className="container-xxl py-4 py-lg-5">
        <div className="container">
          <div className="mx-auto training-tabs-shell mb-4 mb-lg-5">
            <p className="text-center text-muted small mb-3 mb-md-4 px-1">
              Choose a category, then switch between Videos, PDF, and
              Presentations.
            </p>
            <div className="training-tabs-wrap">
              <ul
                className="nav training-tabs flex-column flex-sm-row"
                role="tablist"
              >
                {TAB_KEYS.map((key) => (
                  <li className="nav-item flex-fill" role="presentation" key={key}>
                    <button
                      type="button"
                      className={`nav-link training-tab-btn w-100 ${
                        activeTab === key ? "active" : ""
                      }`}
                      id={`tab-${key}`}
                      role="tab"
                      aria-selected={activeTab === key}
                      aria-current={activeTab === key ? "true" : undefined}
                      onClick={() => setActiveTab(key)}
                    >
                      <span className="training-tab-btn-inner">
                        <span
                          className="training-tab-icon"
                          aria-hidden="true"
                        >
                          <i className={`fas ${TAB_ICONS[key]}`} />
                        </span>
                        <span className="training-tab-label">
                          {TAB_LABELS[key]}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="tab-content training-tab-panel pt-1">
            <div
              className="tab-pane fade show active"
              role="tabpanel"
              aria-labelledby={`tab-${activeTab}`}
            >
              {!hasAnyContent && (
                <h2 className="text-center text-muted py-5">Coming Soon</h2>
              )}

              {hasAnyContent && (
                <>
                  <div
                    className="training-media-pills mb-4"
                    role="tablist"
                    aria-label="Training material type"
                  >
                    <ul className="nav training-media-nav flex-wrap justify-content-center gap-2">
                      {videos.length > 0 && (
                        <li className="nav-item" role="presentation">
                          <button
                            type="button"
                            role="tab"
                            id={`media-videos-${activeTab}`}
                            className={`training-media-pill ${
                              mediaSection === "videos" ? "active" : ""
                            }`}
                            aria-selected={mediaSection === "videos"}
                            onClick={() => setMediaSection("videos")}
                          >
                            <i
                              className="fas fa-video me-2"
                              aria-hidden="true"
                            />
                            Videos
                            <span className="badge rounded-pill ms-2 training-media-pill-count">
                              {videos.length}
                            </span>
                          </button>
                        </li>
                      )}
                      {pdfs.length > 0 && (
                        <li className="nav-item" role="presentation">
                          <button
                            type="button"
                            role="tab"
                            id={`media-pdfs-${activeTab}`}
                            className={`training-media-pill ${
                              mediaSection === "pdfs" ? "active" : ""
                            }`}
                            aria-selected={mediaSection === "pdfs"}
                            onClick={() => setMediaSection("pdfs")}
                          >
                            <i
                              className="fas fa-file-pdf me-2"
                              aria-hidden="true"
                            />
                            PDF
                            <span className="badge rounded-pill ms-2 training-media-pill-count">
                              {pdfs.length}
                            </span>
                          </button>
                        </li>
                      )}
                      {slides.length > 0 && (
                        <li className="nav-item" role="presentation">
                          <button
                            type="button"
                            role="tab"
                            id={`media-slides-${activeTab}`}
                            className={`training-media-pill ${
                              mediaSection === "slides" ? "active" : ""
                            }`}
                            aria-selected={mediaSection === "slides"}
                            onClick={() => setMediaSection("slides")}
                          >
                            <i
                              className="fas fa-file-powerpoint me-2"
                              aria-hidden="true"
                            />
                            Presentations
                            <span className="badge rounded-pill ms-2 training-media-pill-count">
                              {slides.length}
                            </span>
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>

                  <div
                    className="training-media-panel rounded-3 border bg-white p-3 p-md-4"
                    role="tabpanel"
                    aria-labelledby={`media-${mediaSection}-${activeTab}`}
                  >
                    {mediaSection === "videos" && videos.length > 0 && (
                      <div className="row g-4 g-lg-4">
                        {videos.map((file) => (
                          <div
                            key={file}
                            className="col-12 col-lg-6 wow fadeInUp"
                            data-wow-delay="0.1s"
                          >
                            <article className="training-asset-card h-100 d-flex flex-column">
                              <div className="ratio ratio-16x9 bg-dark rounded-3 training-video-ratio">
                                <video
                                  className="position-absolute top-0 start-0 w-100 h-100 rounded-3 training-video-el"
                                  controls
                                  preload="metadata"
                                  playsInline
                                  src={trainingUrl(file)}
                                >
                                  Your browser does not support the video tag.
                                </video>
                              </div>
                              <p className="text-center mb-0 mt-3 fw-semibold text-body small">
                                {displayTitle(file)}
                              </p>
                            </article>
                          </div>
                        ))}
                      </div>
                    )}

                    {mediaSection === "pdfs" && pdfs.length > 0 && (
                      <div className="row g-4">
                        {pdfs.map((file) => (
                          <div
                            key={file}
                            className="col-12 wow fadeInUp"
                            data-wow-delay="0.1s"
                          >
                            <article className="training-asset-card training-asset-card--embed d-flex flex-column h-100">
                              <div className="training-embed-shell rounded-3 overflow-hidden border bg-white">
                                <iframe
                                  title={displayTitle(file)}
                                  className="training-pdf-frame w-100 border-0 d-block"
                                  src={trainingUrl(file)}
                                />
                              </div>
                              <p className="text-center mb-2 mt-3 fw-semibold text-body small">
                                {displayTitle(file)}
                              </p>
                              <p className="text-center small mb-0 mt-auto">
                                <a
                                  href={trainingUrl(file)}
                                  download
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  Download PDF
                                </a>
                              </p>
                            </article>
                          </div>
                        ))}
                      </div>
                    )}

                    {mediaSection === "slides" && slides.length > 0 && (
                      <div className="row g-4">
                        {slides.map((file) => (
                          <div
                            key={file}
                            className="col-12 col-lg-6 wow fadeInUp"
                            data-wow-delay="0.1s"
                          >
                            <article className="training-asset-card training-asset-card--embed d-flex flex-column h-100">
                              {officePreviewOk ? (
                                <div className="training-embed-shell training-embed-shell--slides rounded-3 overflow-hidden border bg-light flex-grow-1">
                                  <iframe
                                    title={displayTitle(file)}
                                    className="training-slide-frame w-100 border-0 d-block"
                                    src={officeEmbedUrl(file)}
                                  />
                                </div>
                              ) : (
                                <div className="training-slide-placeholder rounded-3 border bg-light d-flex align-items-center justify-content-center text-muted small px-3 text-center">
                                  Preview loads when the site is served over a
                                  public URL (Office Online). Use download below
                                  for local development.
                                </div>
                              )}
                              <p className="text-center mb-2 mt-3 fw-semibold text-body small">
                                {displayTitle(file)}
                              </p>
                              <p className="text-center small mb-0 mt-auto">
                                <a
                                  href={trainingUrl(file)}
                                  download
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  Download{" "}
                                  {file.toLowerCase().endsWith(".pptx")
                                    ? "PPTX"
                                    : "PPT"}
                                </a>
                              </p>
                            </article>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Training;
